<template>
  <component
    :is="dialog ? 'q-dialog' : 'div'"
    v-if="shown"
    v-model="shown"
    :maximized="dialog"
    data-test-id="category-dialog"
  >
    <q-card
      v-if="category"
      :class="{ 'bg-transparent no-shadow': !dialog }"
      :style="{
        width: $q.screen.lt.sm || !dialog ? '100%' : '90%',
        height: $q.screen.lt.sm || !dialog ? undefined : '90%'
      }"
    >
      <q-card-section
        class="row items-center q-py-none"
        :class="{
          'q-px-md': $q.screen.lt.sm || dialog,
          'q-px-none': !dialog && $q.screen.gt.xs,
          'q-pt-lg q-pb-sm': dialog
        }"
      >
        <div
          class="text-h6 text-center"
          style="line-height: 1.5;"
        >
          <slot name="title">
            {{ category.name }}
          </slot>
        </div>
        <q-space />
        <q-btn
          v-if="dialog"
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section
        :class="$q.screen.lt.sm || dialog ? 'q-px-md' : 'q-px-none'"
      >
        <slot name="items">
          <div
            class="row q-gutter-md justify-start scroll"
            :class="{
              'item-center self-center': $q.screen.lt.sm && dialog,
              'content-start': !($q.screen.lt.sm && dialog),
              'q-px-md': !$isLandscapeView() && !$q.screen.lt.sm
            }"
          >
            <item-card
              v-for="item in categoryItems"
              :key="item.id"
              :item="item"
              :order="order"
              :display-price="displayPrice"
              :item-width="itemWidth"
              :dialog="dialog"
              :is-mobile="isMobile"
              :hide-image="hideImage"
              :bg-color="'primary'"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
              :has-alcohol-acknowledgement="category.hasAlcoholAcknowledgement"
              @click="adminApproved => createOrderItem(item, adminApproved)"
            />
          </div>
        </slot>
      </q-card-section>
    </q-card>
    <order-item-form
      ref="orderItemForm"
      :display-price="displayPrice"
      :available-online="availableOnline"
      @add-model="orderItem => notifyAdded(orderItem.itemOrderItem.item)"
    />
  </component>
</template>

<script>
import { Notify } from 'quasar'
import ItemCard from 'src/modules/checkout/components/ItemCard'
import AvailableItemsMixin from 'src/modules/checkout/components/mixins/AvailableItemsMixin'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
import OrderItemForm from 'src/modules/register/components/order-item-form/OrderItemForm'

export default {
  name: 'CategoryDialog',
  components: { ItemCard, OrderItemForm },
  mixins: [SalesRestrictionsMixin, AvailableItemsMixin],
  configuration: ['currentSite'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    order: {
      type: Object,
      required: true
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      category: null
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite.activeInventoryStrategy
    },
    categoryItems () {
      const categoryItems = _.sortBy(this.category.items?.data, 'displayOrder')

      return _.filter(categoryItems, item => {
        if (!item.active) return false

        if (this.availableOnline && !item.availableOnline) return false

        if (this.$register) return true

        if (this.inventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer && this.isOutOfStock({ item })) {
          return false
        }

        // If the site does not have online delivery, we can only show ready to serve items
        if (!this.currentSite.hasOnlineDelivery && item.requiresPreparation) return false

        return this.$comsumer ? !item.salesDisabled && this.isItemAvailable(item) : !item.salesDisabled
      })
    },
    itemWidth () {
      if (this.$q.screen.lt.sm) {
        return '45%'
      } else if (this.$q.screen.lt.md) {
        return '22%'
      } else {
        return '18%'
      }
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (this.shown && this.category) {
        this.$trackPage(this, { category: this.category.name })
      }
    }
  },
  methods: {
    hide () {
      this.shown = false
      setTimeout(() => {
        this.category = null
      }, 0)
    },
    show (category) {
      this.shown = true
      setTimeout(() => {
        this.category = category
      }, 0)
    },
    createOrderItem (item, adminApproved = false) {
      let orderItem
      if (item.itemPreference) {
        orderItem = this.order.getFavoriteOrderItem(item.itemPreference)
      } else {
        orderItem = this.order.itemOrderItemForItem(item, null, null, adminApproved)
      }

      // Skip showing detail on register since they can see the cart and can easily edit if needed
      if (item.skipDetailDialog) {
        this.order.orderItems.add(orderItem)
        orderItem.order = this.order
        this.notifyAdded(item)
      } else {
        orderItem.order = this.order
        this.$refs.orderItemForm.setCollection(this.order.orderItems)
        this.$refs.orderItemForm.show(orderItem)
      }
    },
    notifyAdded (item) {
      if (this.dialog) {
        this.shown = false // Close the dialog after adding the item to the cart
      }
      if (this.$register) return
      Notify.create({
        message: `${item.name} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.added-to-cart-notif {
  margin-bottom: 60px;
  margin-top: -50px;
}

.q-dialog__inner--maximized > div {
  // I have no idea why we need to do this, but the dialog doesn't doesn't
  // center vertically if we don't.
  width: unset;
}
</style>
